import { useCallback, useEffect, useMemo, useState } from "react";
import { useParams, useNavigate, useLocation, Link } from "react-router-dom";
import { apiClient } from "../../App";
import { toast } from "sonner";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Badge } from "../../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import {
  ArrowLeft, MapPin, SlidersHorizontal, Check, ImageIcon, Upload,
  X, Trash2, ExternalLink, Calendar, Building2, ArrowUpRight,
} from "lucide-react";
import { SCOPES, SCOPE_BY_KEY, PACKAGE_SCOPES, groupByScope } from "../../components/portfolio/scopes";
import CoverImage from "../../components/portfolio/CoverImage";
import PackageBoard from "../../components/portfolio/PackageBoard";
import ScopeDialog from "../../components/portfolio/ScopeDialog";
import MediaLightbox from "../../components/portfolio/MediaLightbox";
import AddLinkDialog from "../../components/portfolio/AddLinkDialog";
import MediaEditDialog from "../../components/portfolio/MediaEditDialog";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

// Grids ask for `thumb`; the viewer and downloads take the original. Delivered
// renders are large files and a section can hold dozens of them.
const mediaUrl = (m, size) =>
  m?.id ? `${API}/portfolio/media/${m.id}/download${size ? `?size=${size}` : ""}` : null;

/**
 * The project record — everything the studio delivered for one development,
 * organised by the department that produced it.
 *
 * The whole project fits on one screen: a title block and a rail of package
 * cards, one per scope, each fronted by a still from the work. A card opens its
 * deliverables in a dialog rather than sending the reader down a page of
 * stacked sections — nobody scrolls past four scopes to reach the fifth.
 *
 * One page serves both audiences. A PM flips Organize on and the same cards
 * grow a sold/not-sold toggle and open onto upload targets, link buttons and
 * per-scope notes — so what gets filled in is exactly what gets seen.
 */
export default function PortfolioProjectPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const routeLocation = useLocation();

  // /portfolio/:id reads; /portfolio/:id/edit organizes. The URL is the mode, so
  // a half-organized page survives a refresh and can be handed to a colleague.
  const organizeRoute = routeLocation.pathname.endsWith("/edit");

  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [canCurate, setCanCurate] = useState(false);
  const [uploadingScope, setUploadingScope] = useState(null);
  const [addLinkScope, setAddLinkScope] = useState(null);
  const [editingItem, setEditingItem] = useState(null);
  const [lightbox, setLightbox] = useState({ items: [], index: null });
  const [openScopeKey, setOpenScopeKey] = useState(null);

  const load = useCallback(async () => {
    try {
      const res = await apiClient.get(`/portfolio/projects/${id}`);
      setProject(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to load this project");
      navigate("/portfolio");
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    apiClient.get("/portfolio/scopes")
      .then(res => setCanCurate(Boolean(res.data?.can_curate)))
      .catch(() => setCanCurate(false));
  }, []);

  // Someone who can't curate never sees organize mode, even on /edit directly.
  const organizing = organizeRoute && canCurate;
  const setOrganizing = (on) =>
    navigate(on ? `/portfolio/${id}/edit` : `/portfolio/${id}`, { replace: true });

  const groups = useMemo(() => groupByScope(project?.media || []), [project]);
  const counts = useMemo(() => {
    const c = {};
    SCOPES.forEach(s => { c[s.key] = groups[s.key].all.length; });
    return c;
  }, [groups]);
  const included = project?.included_scopes || [];
  const openScope = openScopeKey ? SCOPE_BY_KEY[openScopeKey] : null;

  // Nothing sold and nothing on file — there are no cards worth showing a
  // reader, so the page offers to start instead of laying out six dead frames.
  const hasPackage = useMemo(
    () => PACKAGE_SCOPES.some(s => counts[s.key] > 0 || included.includes(s.key)),
    [counts, included]
  );
  const showRail = organizing || hasPackage;
  const showFiles = organizing || counts.files > 0;

  /* ── mutations ─────────────────────────────────────────────────────── */

  async function patchProject(patch, { silent } = {}) {
    try {
      await apiClient.put(`/portfolio/projects/${id}`, patch);
      if (!silent) toast.success("Saved");
      return true;
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't save");
      await load();
      return false;
    }
  }

  async function toggleIncludedScope(key) {
    const next = included.includes(key) ? included.filter(k => k !== key) : [...included, key];
    setProject(p => ({ ...p, included_scopes: next }));  // optimistic — the board must feel instant
    await patchProject({ included_scopes: next }, { silent: true });
  }

  async function saveScopeNote(key, text) {
    setProject(p => ({ ...p, scope_notes: { ...(p.scope_notes || {}), [key]: text } }));
    await patchProject({ scope_notes: { [key]: text } }, { silent: true });
  }

  async function uploadFiles(scopeKey, files) {
    if (!files?.length) return;
    setUploadingScope(scopeKey);
    let failed = 0;
    for (const file of Array.from(files)) {
      try {
        const fd = new FormData();
        fd.append("file", file);
        await apiClient.post(
          `/portfolio/projects/${id}/upload?scope=${scopeKey}`,
          fd,
          { headers: { "Content-Type": "multipart/form-data" } }
        );
      } catch (err) {
        failed += 1;
        toast.error(`${file.name} — ${err.response?.data?.detail || "upload failed"}`);
      }
    }
    const uploaded = files.length - failed;
    if (uploaded > 0) toast.success(`${uploaded} file${uploaded === 1 ? "" : "s"} added`);
    setUploadingScope(null);
    await load();
  }

  async function addLink(payload) {
    try {
      await apiClient.post(`/portfolio/projects/${id}/media`, payload);
      await load();
      toast.success("Added");
      return true;
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't add that link");
      return false;
    }
  }

  async function saveItem(mediaId, patch) {
    try {
      await apiClient.put(`/portfolio/media/${mediaId}`, patch);
      await load();
      toast.success("Saved");
      return true;
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't save that item");
      return false;
    }
  }

  async function deleteItem(item) {
    const name = item.title || item.file_name || "this item";
    if (!window.confirm(`Remove ${name} from this project? This can't be undone.`)) return;
    try {
      await apiClient.delete(`/portfolio/media/${item.id}`);
      await load();
      toast.success("Removed");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't remove that item");
    }
  }

  async function setCover(item) {
    try {
      await apiClient.put(`/portfolio/projects/${id}/hero/${item.id}`);
      await load();
      toast.success("Cover updated");
    } catch (err) {
      toast.error("Couldn't set the cover");
    }
  }

  async function reorder(mediaIds) {
    try {
      await apiClient.put(`/portfolio/projects/${id}/media-order`, { media_ids: mediaIds });
      await load();
    } catch (err) {
      toast.error("Couldn't save that order");
      await load();
    }
  }

  /* ── render ────────────────────────────────────────────────────────── */

  if (loading) {
    return (
      <div className="space-y-8">
        <div className="aspect-[21/9] rounded-2xl bg-secondary animate-pulse" />
        <div className="h-24 rounded-xl bg-secondary animate-pulse" />
      </div>
    );
  }
  if (!project) return null;

  return (
    <div className="space-y-8 pb-16" data-testid="portfolio-project-page">
      {/* Top rail */}
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-2 min-w-0">
          <Button variant="ghost" size="icon" onClick={() => navigate("/portfolio")} className="rounded-full shrink-0">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <Link to="/portfolio" className="micro-label hover:text-foreground transition-smooth">Portfolio</Link>
          <span className="text-muted-foreground/40">/</span>
          <span className="micro-label text-foreground truncate">{project.title}</span>
        </div>

        <div className="flex items-center gap-2 shrink-0">
          {project.status !== "Published" && (
            <Badge variant="outline" className="micro-label">{project.status}</Badge>
          )}
          {canCurate && (
            <Button
              variant={organizing ? "default" : "outline"}
              className="rounded-full"
              onClick={() => setOrganizing(!organizing)}
              data-testid="organize-toggle"
            >
              {organizing ? <><Check className="w-4 h-4 mr-2" />Done</> : <><SlidersHorizontal className="w-4 h-4 mr-2" />Organize</>}
            </Button>
          )}
        </div>
      </div>

      {/* Title block. No cover banner — the package cards are right below and
          already wear the work, so a hero would just repeat the first still. */}
      <div className="pb-6 border-b border-border">
        {project.project_type && <span className="micro-label">{project.project_type}</span>}
        <h1 className="heading-1 mt-2">{project.title}</h1>
        <div className="flex items-center gap-x-5 gap-y-1.5 flex-wrap mt-3 text-sm text-muted-foreground">
          {project.client_name && (
            <span className="flex items-center gap-1.5"><Building2 className="w-3.5 h-3.5" />{project.client_name}</span>
          )}
          {project.location && (
            <span className="flex items-center gap-1.5"><MapPin className="w-3.5 h-3.5" />{project.location}</span>
          )}
          {project.delivered_on && (
            <span className="flex items-center gap-1.5 tabular"><Calendar className="w-3.5 h-3.5" />{formatDelivered(project.delivered_on)}</span>
          )}
          {project.source_program_id && (
            <Link
              to={`/programs/${project.source_program_id}`}
              className="flex items-center gap-1.5 hover:text-foreground transition-smooth"
            >
              <ExternalLink className="w-3.5 h-3.5" />Open the project in Programs
            </Link>
          )}
        </div>
      </div>

      {/* Description + tags, or the whole details form in organize mode */}
      {organizing ? (
        <DetailsPanel project={project} onSave={patchProject} onReload={load} onDeleted={() => navigate("/portfolio")} />
      ) : (
        (project.short_description || project.tags?.length > 0) && (
          <div className="space-y-3 max-w-3xl">
            {project.short_description && <p className="body-text">{project.short_description}</p>}
            {project.tags?.length > 0 && (
              <div className="flex gap-1.5 flex-wrap">
                {project.tags.map(t => <Badge key={t} variant="outline" className="micro-label">{t}</Badge>)}
              </div>
            )}
          </div>
        )
      )}

      {showRail && (
        <PackageBoard
          groups={groups}
          included={included}
          organizing={organizing}
          onToggle={toggleIncludedScope}
          onOpen={setOpenScopeKey}
          mediaUrl={mediaUrl}
        />
      )}

      {/* Project Files isn't part of a package — it's the master folder and
          whatever doesn't sit in one scope, so it gets a rule, not a card. */}
      {showFiles && <FilesRow count={counts.files} onOpen={() => setOpenScopeKey("files")} />}

      {!showRail && !showFiles && (
        <div className="text-center py-16 border border-dashed border-border rounded-xl">
          <p className="text-sm text-muted-foreground">Nothing has been recorded for this project yet.</p>
          {canCurate && (
            <Button className="rounded-full mt-4" onClick={() => setOrganizing(true)}>
              <SlidersHorizontal className="w-4 h-4 mr-2" />Start organizing
            </Button>
          )}
        </div>
      )}

      {openScope && (
        <ScopeDialog
          scope={openScope}
          group={groups[openScope.key]}
          inPackage={included.includes(openScope.key)}
          organizing={organizing}
          note={project.scope_notes?.[openScope.key] || ""}
          lightboxOpen={lightbox.index !== null}
          onClose={() => setOpenScopeKey(null)}
          onNoteSave={saveScopeNote}
          onUpload={uploadFiles}
          onAddLink={setAddLinkScope}
          onEditItem={setEditingItem}
          onDeleteItem={deleteItem}
          onSetCover={setCover}
          onReorder={reorder}
          onOpenImage={(items, index) => setLightbox({ items, index })}
          heroImageId={project.hero_image_id}
          uploading={uploadingScope === openScope.key}
          mediaUrl={mediaUrl}
        />
      )}

      <MediaLightbox
        items={lightbox.items}
        index={lightbox.index}
        mediaUrl={mediaUrl}
        onClose={() => setLightbox({ items: [], index: null })}
        onIndexChange={(index) => setLightbox(l => ({ ...l, index }))}
      />

      <AddLinkDialog
        open={Boolean(addLinkScope)}
        scope={addLinkScope}
        onClose={() => setAddLinkScope(null)}
        onSubmit={addLink}
      />

      {editingItem && (
        <MediaEditDialog item={editingItem} onClose={() => setEditingItem(null)} onSave={saveItem} />
      )}
    </div>
  );
}

/** The catch-all scope, as one hairline row under the package. It carries the
 *  master delivery folder — useful, never a selling point. */
function FilesRow({ count, onOpen }) {
  const scope = SCOPE_BY_KEY.files;
  return (
    <button
      type="button"
      onClick={onOpen}
      className="w-full flex items-center justify-between gap-4 px-5 py-4 rounded-xl border border-border bg-card card-hover text-left transition-smooth"
      data-testid="package-open-files"
    >
      <span className="flex items-center gap-3 min-w-0">
        <scope.Icon className="w-4 h-4 text-muted-foreground shrink-0" strokeWidth={1.5} />
        <span className="micro-label text-foreground">{scope.label}</span>
        <span className="text-xs text-muted-foreground truncate">
          {count > 0 ? `${count} item${count === 1 ? "" : "s"}` : "Nothing filed here yet"}
        </span>
      </span>
      <ArrowUpRight className="w-4 h-4 text-muted-foreground shrink-0" />
    </button>
  );
}

/* ─────────────────────── project details (organize) ─────────────────── */

function DetailsPanel({ project, onSave, onReload, onDeleted }) {
  const [form, setForm] = useState({
    title: project.title || "",
    client_name: project.client_name || "",
    location: project.location || "",
    project_type: project.project_type || "",
    delivered_on: project.delivered_on || "",
    short_description: project.short_description || "",
    status: project.status || "Draft",
  });
  const [tags, setTags] = useState(project.tags || []);
  const [tagInput, setTagInput] = useState("");
  const [categories, setCategories] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    apiClient.get("/portfolio/categories").then(res => setCategories(res.data)).catch(() => {});
  }, []);

  const set = (field) => (e) => setForm(f => ({ ...f, [field]: e.target.value }));

  const save = async () => {
    if (!form.title.trim()) {
      toast.error("A project needs a title");
      return;
    }
    setSaving(true);
    const ok = await onSave({ ...form, tags });
    setSaving(false);
    if (ok) onReload();
  };

  const removeProject = async () => {
    if (!window.confirm(`Delete ${project.title} and everything filed under it? This can't be undone.`)) return;
    try {
      await apiClient.delete(`/portfolio/projects/${project.id}`);
      toast.success("Project deleted");
      onDeleted();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't delete this project");
    }
  };

  const addTag = () => {
    const value = tagInput.trim();
    if (value && !tags.includes(value)) setTags([...tags, value]);
    setTagInput("");
  };

  return (
    <div className="rounded-xl border border-border p-5 sm:p-6" data-testid="project-details-panel">
      <h2 className="micro-label mb-5">Project details</h2>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        <Field label="Title">
          <Input value={form.title} onChange={set("title")} data-testid="details-title" />
        </Field>
        <Field label="Client">
          <Input value={form.client_name} onChange={set("client_name")} placeholder="e.g. Terra Group" />
        </Field>
        <Field label="Location">
          <Input value={form.location} onChange={set("location")} placeholder="e.g. Aventura, FL" />
        </Field>
        <Field label="Category">
          <Select value={form.project_type} onValueChange={(v) => setForm(f => ({ ...f, project_type: v }))}>
            <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
            <SelectContent>
              {[...new Set([form.project_type, ...categories].filter(Boolean))].map(c => (
                <SelectItem key={c} value={c}>{c}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </Field>
        <Field label="Delivered">
          <Input value={form.delivered_on} onChange={set("delivered_on")} placeholder="e.g. 2026 or 2026-04-18" />
        </Field>
        <Field label="Visibility">
          <Select value={form.status} onValueChange={(v) => setForm(f => ({ ...f, status: v }))}>
            <SelectTrigger data-testid="details-status"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="Published">Published — the whole team sees it</SelectItem>
              <SelectItem value="Draft">Draft — curators only</SelectItem>
              <SelectItem value="Archived">Archived</SelectItem>
            </SelectContent>
          </Select>
        </Field>
      </div>

      <div className="mt-4">
        <Field label="Description">
          <Textarea value={form.short_description} onChange={set("short_description")} rows={2} />
        </Field>
      </div>

      <div className="mt-4">
        <CoverField project={project} onReload={onReload} />
      </div>

      <div className="mt-4">
        <Label className="micro-label">Tags</Label>
        <div className="flex gap-2 mt-2 max-w-md">
          <Input
            value={tagInput}
            onChange={(e) => setTagInput(e.target.value)}
            placeholder="Add a tag…"
            onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addTag(); } }}
          />
          <Button type="button" variant="outline" className="rounded-full" onClick={addTag}>Add</Button>
        </div>
        {tags.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mt-2.5">
            {tags.map(t => (
              <button
                key={t}
                onClick={() => setTags(tags.filter(x => x !== t))}
                className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full border border-border text-xs hover:border-terracotta-300 transition-smooth"
              >
                {t}<X className="w-3 h-3" />
              </button>
            ))}
          </div>
        )}
      </div>

      <div className="flex items-center justify-between mt-6 pt-5 border-t border-border">
        <Button variant="ghost" onClick={removeProject} className="rounded-full text-terracotta-600 hover:text-terracotta-700">
          <Trash2 className="w-4 h-4 mr-2" />Delete project
        </Button>
        <Button onClick={save} disabled={saving} className="rounded-full" data-testid="details-save">
          {saving ? "Saving…" : "Save details"}
        </Button>
      </div>
    </div>
  );
}

/** The cover picker: what the gallery card wears, changeable in one click.
 *  An upload lands in Renderings (a cover is a still of the work, so it
 *  belongs with the deliverables) and becomes the cover at the same time. */
function CoverField({ project, onReload }) {
  const [uploading, setUploading] = useState(false);
  const hero = project.hero_image;

  const upload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await apiClient.post(
        `/portfolio/projects/${project.id}/upload?scope=renders&set_as_hero=true`,
        fd,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      toast.success("Cover updated");
      onReload();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't upload that image");
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="space-y-1.5">
      <Label className="micro-label">Cover image</Label>
      <div className="flex items-center gap-4">
        <div className="w-36 aspect-video rounded-lg overflow-hidden border border-border bg-secondary shrink-0">
          <CoverImage
            sources={[mediaUrl(hero, "thumb"), mediaUrl(hero)]}
            alt="Current cover"
            className="w-full h-full object-cover"
            fallback={
              <div className="w-full h-full flex items-center justify-center">
                <ImageIcon className="w-5 h-5 text-muted-foreground/50" strokeWidth={1.5} />
              </div>
            }
          />
        </div>
        <div className="space-y-1.5">
          <Button asChild variant="outline" className="rounded-full cursor-pointer" disabled={uploading}>
            <label>
              <Upload className="w-4 h-4 mr-2" />
              {uploading ? "Uploading…" : "Upload cover"}
              <input
                type="file"
                accept="image/jpeg,image/png,image/webp,image/gif"
                className="hidden"
                onChange={upload}
                disabled={uploading}
                data-testid="cover-upload-input"
              />
            </label>
          </Button>
          <p className="text-xs text-muted-foreground">
            Fronts the gallery card. You can also pick any rendering inside a scope.
          </p>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div className="space-y-1.5">
      <Label className="micro-label">{label}</Label>
      {children}
    </div>
  );
}

function formatDelivered(value) {
  // Accepts a year, a month, or a full ISO date — shows whatever precision it has.
  const iso = /^(\d{4})-(\d{2})-(\d{2})/.exec(value);
  if (iso) {
    return new Date(`${iso[0]}T00:00:00`).toLocaleDateString(undefined, {
      month: "long", year: "numeric",
    });
  }
  return value;
}
